/**
 * UNION ARENA TCG - Complete Game Configuration
 *
 * This is the ONLY file you need to modify for Union Arena-specific settings.
 * Everything about the game is configured here.
 */

import { UAHandler } from "./handler";
import {
  getCardData,
  getAACardIdData,
  getCardImage,
  getAAFromCardId,
} from "@/lib/games/unionArena/getCardDataLocal";
import { GAME_DOMAINS } from "@/games/domains";

export const unionArenaGame = {
  // ============================================================================
  // BASIC INFO
  // ============================================================================
  id: "union-arena" as const,
  name: "Union Arena TCG",
  abbreviation: "UA TCG",
  gameId: 2, // Database game ID

  // ============================================================================
  // SEO & METADATA
  // ============================================================================
  seo: {
    title: "Union Arena TCG Meta Decklists & Card DB",
    description:
      "Discover the world of Union Arena TCG with TCGfan. Explore meta decklists, card database, and stay updated with the latest strategies for your favorite anime series including Bleach, Hunter X Hunter, Jujutsu Kaisen, and more. Whether you're a competitive player or just starting out, find everything you need to master Union Arena TCG.",
  },

  // ============================================================================
  // GAME MECHANICS
  // ============================================================================
  mechanics: {
    hasLeader: false,
    hasLeaderDistribution: false,
    hasPieChart: false,
    hasFormatReleaseDates: false,
    maxCopiesDefault: 4,
    hasLifeProperty: false,
    leaderType: "Leader",
  },

  tcgplayer: {
    categoryId: 81,
    productLine: "Union Arena",
  },

  routePrefix: "union-arena/",

  // ============================================================================
  // FORMATS & SETS
  // ============================================================================
  formats: {
    east: ["SMD", "RNK_MGS"],
    west: ["AOT_CGH2", "FMA"],
  },

  /** Public decklist-submission form, if the game accepts submissions. */
  submitFormUrl: "https://forms.gle/Y9Wgdrpmpuhvf6Ta6",

  // ============================================================================
  // NAVIGATION & UI
  // ============================================================================
  navigation: {
    /** The game's deck builder, or undefined if it has none yet. */
    deckbuilderHref: undefined as string | undefined,
    showMarketWatch: true,
    marketWatchHref: "/union-arena/market-watch",
    showTools: false,
    showBoosters: false,
    showStarters: false,
    showSearch: false,
  },

  // ============================================================================
  // DOMAIN CONFIGURATION
  // ============================================================================
  domain: {
    ...GAME_DOMAINS["union-arena"],
    disabledRoutes: [] as string[],
  },

  // ============================================================================
  // GAME HANDLER & DECK BUILDER
  // ============================================================================
  handler: () => new UAHandler(),

  deckBuilder: {
    getCardData,
    getAACardIdData,
    getCardImage,
    getAAFromCardId,
  },

  // ============================================================================
  // CDN CONFIGURATION
  // ============================================================================
  cdn: {
    // Union Arena doesn't use leader squares currently
    leaderSquaresPath: undefined,
  } as { leaderSquaresPath?: string },

  // ============================================================================
  // INITIAL STATE FOR STORES (auto-generated, no need to modify)
  // ============================================================================
  initialState: {
    prices: null,
    loading: false,
    decks: [],
    cardOrderIds: [],
    filters: {},
  },
};

export type UnionArenaGame = typeof unionArenaGame;
